import { useState } from "react";

const CATEGORIES = [
  "Inaccurate or misleading information",
  "Inappropriate or offensive content",
  "Biased or discriminatory content",
  "Privacy violation",
  "Other",
];

export default function AIReportModal({ onClose, context }) {
  const [category,     setCategory]     = useState("");
  const [details,      setDetails]      = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [error,        setError]        = useState("");

  const handleSubmit = async () => {
    if (!category) { setError("Please select a category."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch("/api/report-ai-content", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, details, context }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || "Failed to send report. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%", background: "var(--bg-hover)", border: "1px solid var(--border)",
    borderRadius: 9, padding: "9px 12px", color: "var(--text-primary)",
    fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:800, background:"rgba(0,0,0,0.5)", backdropFilter:"blur(2px)" }} />

      {/* Modal */}
      <div style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:801, width:"calc(100% - 48px)", maxWidth:480, background:"var(--bg-secondary)", border:"1px solid var(--border)", borderRadius:18, padding:"28px 28px 24px", boxShadow:"0 16px 48px rgba(0,0,0,0.5)", fontFamily:"'DM Sans',sans-serif" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:"var(--text-primary)", marginBottom:4 }}>Report AI-Generated Content</div>
            <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>
              Help us improve Vorsia's AI by reporting inappropriate or inaccurate content.
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--text-tertiary)", cursor:"pointer", fontSize:18, padding:4, flexShrink:0, marginLeft:12 }}>✕</button>
        </div>

        {submitted ? (
          <div style={{ textAlign:"center", padding:"24px 0" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:14, fontWeight:600, color:"#4ade80", marginBottom:8 }}>Report submitted</div>
            <div style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:20 }}>
              Thank you for helping keep Vorsia's AI safe. We review all reports within 48 hours.
            </div>
            <button onClick={onClose} style={{ background:"var(--accent)", border:"none", color:"#fff", padding:"10px 24px", borderRadius:9, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Category */}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11, fontWeight:700, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:1, marginBottom:8, display:"block" }}>
                Issue Type <span style={{ color:"var(--accent)" }}>*</span>
              </label>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setCategory(cat)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:9, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13, textAlign:"left", background:category===cat?"var(--accent-soft)":"var(--bg-hover)", border:`1px solid ${category===cat?"rgba(123,111,232,0.4)":"var(--border)"}`, color:category===cat?"var(--accent)":"var(--text-secondary)" }}>
                    <div style={{ width:14, height:14, borderRadius:"50%", border:`2px solid ${category===cat?"var(--accent)":"var(--border)"}`, background:category===cat?"var(--accent)":"transparent", flexShrink:0 }} />
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Details */}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11, fontWeight:700, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:1, marginBottom:8, display:"block" }}>
                Additional Details <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, color:"var(--text-tertiary)" }}>(optional)</span>
              </label>
              <textarea
                value={details}
                onChange={e => setDetails(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="Describe the issue with the AI-generated content..."
                style={{ ...inputStyle, resize:"none", lineHeight:1.6 }}
              />
              <div style={{ fontSize:10, color:"var(--text-tertiary)", textAlign:"right", marginTop:4 }}>{details.length}/500</div>
            </div>

            {error && (
              <div style={{ fontSize:12, color:"#f87171", marginBottom:12, padding:"8px 12px", background:"rgba(248,113,113,0.08)", borderRadius:8, border:"1px solid rgba(248,113,113,0.2)" }}>
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleSubmit} disabled={submitting}
                style={{ flex:2, background:"var(--accent)", border:"none", color:"#fff", padding:"11px", borderRadius:9, fontWeight:700, fontSize:13, cursor:submitting?"not-allowed":"pointer", fontFamily:"'DM Sans',sans-serif", opacity:submitting?0.7:1 }}>
                {submitting ? "Submitting…" : "Submit Report"}
              </button>
              <button onClick={onClose}
                style={{ flex:1, background:"var(--bg-hover)", border:"1px solid var(--border)", color:"var(--text-secondary)", padding:"11px", borderRadius:9, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                Cancel
              </button>
            </div>

            <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:12, textAlign:"center", lineHeight:1.5 }}>
              Reports are sent to contact@vorsia.ai and reviewed within 48 hours.
            </div>
          </>
        )}
      </div>
    </>
  );
}
